import React, { useState, useEffect } from 'react';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import matchSorter from 'match-sorter';
import DespachoModal from './DespachoModal';
import DespachoDetalle from './DespachoDetalle';
import MisReportes from './MisReportes';
import './Despacho.css';

/**
 * DespachoTurno
 * Componente para gestionar apertura/cierre de turnos y acceso a despachos
 * Patrón: Componente funcional con Hooks
 */
const DespachoTurno = ({ usuario, turnoActivo: turnoActivoProp, onTurnoAbierto, onTurnoCerrado, onError }) => {
  const [turnoActivo, setTurnoActivo] = useState(turnoActivoProp || null);
  const [despachos, setDespachos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);
  const [modalAbrirTurno, setModalAbrirTurno] = useState(false);
  const [modalDetalleOpen, setModalDetalleOpen] = useState(false);
  const [modalMisReportesOpen, setModalMisReportesOpen] = useState(false);
  const [idDespachoSeleccionado, setIdDespachoSeleccionado] = useState(null);
  const [selectedDespachoEstado, setSelectedDespachoEstado] = useState(null);
  const [observacionTurno, setObservacionTurno] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('PENDIENTE');
  const [despachosProcesando, setDespachosProcesando] = useState({});
  const [fechaInicio, setFechaInicio] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [turnoCollapsed, setTurnoCollapsed] = useState(true);

  useEffect(() => {
    // Si el padre controla el turno, evitar una segunda carga de turno en este componente.
    if (typeof turnoActivoProp !== 'undefined') {
      setTurnoActivo(turnoActivoProp || null);
      if (turnoActivoProp && turnoActivoProp.IdTurnoDespacho) {
        cargarDespachos(turnoActivoProp.IdTurnoDespacho, filtroEstado, fechaInicio);
      } else {
        setDespachos([]);
      }
    } else {
      cargarTurnoActivo();
    }
  }, [turnoActivoProp, fechaInicio]);

  useEffect(() => {
    if (turnoActivo) {
      setTurnoCollapsed(true);
    }
  }, [turnoActivo]);

  // Auto-refresh de despachos cada 60 segundos cuando hay turno activo
  useEffect(() => {
    if (!turnoActivo || !turnoActivo.IdTurnoDespacho) return;

    const intervalId = setInterval(() => {
      cargarDespachos(turnoActivo.IdTurnoDespacho, filtroEstado, fechaInicio);
    }, 60000); // 60s

    return () => clearInterval(intervalId);
  }, [turnoActivo, filtroEstado, fechaInicio]);

  const cargarTurnoActivo = async () => {
    setCargando(true);
    try {
      const response = await fetch('/api/despacho/turno/activo', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success === true && data.turno) {
        setTurnoActivo(data.turno);
        cargarDespachos(data.turno.IdTurnoDespacho, filtroEstado, fechaInicio);
      } else {
        setTurnoActivo(null);
      }
    } catch (err) {
      console.error('Error al cargar turno activo:', err);
      setTurnoActivo(null);
    } finally {
      setCargando(false);
    }
  };

  const cargarDespachos = async (idTurno, estado = null, fechaInicioParam = null) => {
    setCargando(true);
    try {
      let url = `/api/despacho/listar/${idTurno}`;
      const params = [];
      if (estado) params.push(`estado=${estado}`);
      if (fechaInicioParam) params.push(`fechaInicio=${encodeURIComponent(fechaInicioParam)}`);
      if (params.length) url += `?${params.join('&')}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.success && Array.isArray(data.despachos)) {
        setDespachos(data.despachos);
        setError(null);
      } else {
        setDespachos([]);
      }
    } catch (err) {
      setError(err.message);
      setDespachos([]);
    } finally {
      setCargando(false);
    }
  };

  const handleAbrirTurno = async () => {
    setCargando(true);
    try {
      const response = await fetch('/api/despacho/turno/abrir', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          observacion: observacionTurno
        })
      });

      const data = await response.json();
      
      if (data.success === true && data.turno) {
        setTurnoActivo(data.turno);
        setModalAbrirTurno(false);
        setObservacionTurno('');
        setExito('Turno abierto exitosamente');
        setError(null);
        setTimeout(() => setExito(null), 3000);
        if (onTurnoAbierto) {
          onTurnoAbierto(data.turno);
        }
      } else {
        setError(data.message || 'Error al abrir turno');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  const handleCerrarTurno = async () => {
    if (!turnoActivo || !turnoActivo.IdTurnoDespacho) {
      setError('Turno no válido');
      return;
    }

    if (!window.confirm('¿Está seguro de que desea cerrar este turno?')) {
      return;
    }

    setCargando(true);
    try {
      const response = await fetch(`/api/despacho/turno/cerrar/${turnoActivo.IdTurnoDespacho}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      if (data.success === true) {
        setTurnoActivo(null);
        setDespachos([]);
        setExito('Turno cerrado exitosamente');
        setError(null);
        setTimeout(() => setExito(null), 3000);
        if (onTurnoCerrado) {
          onTurnoCerrado();
        }
      } else {
        setError(data.message || 'Error al cerrar turno');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  const handleFiltroEstado = (estado) => {
    const nuevoFiltro = filtroEstado === estado ? null : estado;
    setFiltroEstado(nuevoFiltro);
    
    if (turnoActivo) {
      cargarDespachos(turnoActivo.IdTurnoDespacho, nuevoFiltro, fechaInicio);
    }
  };

  const getEstadoBadgeClass = (estado) => {
    const clases = {
      'PENDIENTE': 'badge-warning',
      'EN_PROCESO': 'badge-info',
      'DESPACHADO': 'badge-success',
      'ANULADO': 'badge-danger'
    };
    return clases[estado] || 'badge-secondary';
  };

  const handleVerDetalle = (idDespacho) => {
    const despacho = despachos.find(d => String(d.IdDespacho) === String(idDespacho));
    setSelectedDespachoEstado(despacho ? despacho.Estado : null);
    setIdDespachoSeleccionado(idDespacho);
    setModalDetalleOpen(true);
  };

  const handleCerrarDetalle = () => {
    setModalDetalleOpen(false);
    setIdDespachoSeleccionado(null);
    setSelectedDespachoEstado(null);
  };

  const cambiarEstadoDespacho = async (idDespacho, nuevoEstado, observacion = '', estadoEsperado = null) => {
    setCargando(true);
    console.log('[DespachoTurno] cambiarEstadoDespacho', { idDespacho, nuevoEstado, observacion, estadoEsperado });
    try {
      const response = await fetch(`/api/despacho/${idDespacho}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nuevoEstado, observacion, estadoEsperado })
      });

      const data = await response.json();

      if (data.success) {
        console.log('[DespachoTurno] cambiarEstadoDespacho OK', data);
        setExito(data.message || 'Estado actualizado');
        setError(null);
        setTimeout(() => setExito(null), 3000);
        if (turnoActivo && turnoActivo.IdTurnoDespacho) {
          cargarDespachos(turnoActivo.IdTurnoDespacho, filtroEstado, fechaInicio);
        }
        return true;
      } else {
        console.warn('[DespachoTurno] cambiarEstadoDespacho ERROR', data);
        setError(data.message || 'Error al actualizar estado');
        return false;
      }
    } catch (err) {
      console.error('[DespachoTurno] cambiarEstadoDespacho CATCH', err);
      setError(err.message);
      return false;
    } finally {
      setCargando(false);
    }
  };

  const handleProcesar = async (idDespacho) => {
    const key = String(idDespacho);
    if (despachosProcesando[key]) return;

    setDespachosProcesando(prev => ({
      ...prev,
      [key]: true
    }));

    try {
      const consultarResponse = await fetch(`/api/despacho/consultar/${idDespacho}`);
      const consultarData = await consultarResponse.json();

      if (!consultarResponse.ok || !consultarData.success) {
        setError('Error al validar despacho');
        return;
      }

      if (consultarData.existe) {
        setError('Este despacho ya está siendo procesado por otro usuario. No es posible continuar.');
        return;
      }

      const ok = await cambiarEstadoDespacho(idDespacho, 'EN_PROCESO', '', 'PENDIENTE');
      if (ok) {
        setDespachos(prev => prev.map(d => (
          String(d.IdDespacho) === key
            ? { ...d, Estado: 'EN_PROCESO' }
            : d
        )));
        setSelectedDespachoEstado('EN_PROCESO');
        setIdDespachoSeleccionado(idDespacho);
        setModalDetalleOpen(true);
      }
    } finally {
      setDespachosProcesando(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleAnular = async (idDespacho) => {
    if (!window.confirm('¿Confirma anular este despacho?')) return;
    await cambiarEstadoDespacho(idDespacho, 'ANULADO');
  };

  const handleTerminar = async (idDespacho) => {
    console.log('[DespachoTurno] handleTerminar clicked', idDespacho);
    const ok = await cambiarEstadoDespacho(idDespacho, 'DESPACHADO');
    if (ok) {
      setModalDetalleOpen(false);
      setIdDespachoSeleccionado(null);
      setSelectedDespachoEstado(null);
    }
  };

  const columns = [
    {
      Header: 'N°',
      accessor: 'IdDespacho',
      filterMethod: (filter, rows) =>
        matchSorter(rows, filter.value, { keys: ['IdDespacho'] }),
      filterAll: true,
      Cell: props => <span>{props.value || 0}</span>,
      style: { alignSelf: 'center', paddingInline: '20px', textAlign: 'center' }
    },
    {
      Header: 'Cliente',
      accessor: 'NombreCliente',
      filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ['NombreCliente', 'Cliente'] }),
      filterAll: true,
      Cell: props => props.original.NombreCliente || props.original.Cliente || '-',
      style: { alignSelf: 'center', paddingInline: '20px' }
    },
    {
      Header: 'Comprobante',
      accessor: 'NumeroComprobante',
      filterMethod: (filter, rows) =>
        matchSorter(rows, filter.value, {
          keys: [
            row => {
              const original = row && (row._original || row.original || row);
              const serie = (original && original.SerieComprobante) || '';
              const numero = (original && (original.NumeroComprobante || original.NumComprobante)) || '';
              return `${serie} ${numero} ${serie}-${numero}`.trim();
            }
          ]
        }),
      filterAll: true,
      Cell: props => {
        const serie = props.original && (props.original.SerieComprobante || '');
        const numero = props.original && (props.original.NumeroComprobante || props.original.NumComprobante || '');
        const comprobante = serie ? `${serie} - ${numero}` : (numero || ' - ');
        return <span>{comprobante}</span>;
      },
      style: { alignSelf: 'center', paddingInline: '20px' }
    },
    {
      Header: 'Total',
      accessor: 'Total',
      filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ['Total'] }),
      filterAll: true,
      Cell: props => <span>S/. {parseFloat(props.value || 0).toFixed(2)}</span>,
      style: { alignSelf: 'center', paddingInline: '20px', textAlign: 'right' }
    },
    {
      Header: 'Productos',
      accessor: 'CantidadItems',
      filterMethod: (filter, rows) =>
        matchSorter(rows, filter.value, { keys: ['CantidadItems'] }),
      filterAll: true,
      Cell: props => <span>{props.value || 0}</span>,
      style: { alignSelf: 'center', paddingInline: '20px', textAlign: 'center' }
    },
    {
      Header: 'Fecha',
      accessor: 'FechaRegistro',
      filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ['FechaRegistro'] }),
      filterAll: true,
      Cell: props => (props.value ? new Date(props.value).toLocaleString('es-PE') : '-'),
      style: { alignSelf: 'center', paddingInline: '20px' }
    },
    {
      Header: 'Estado',
      accessor: 'Estado',
      width: 120,
      filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ['Estado'] }),
      filterAll: true,
      Cell: props => (
        <span className={`badge ${getEstadoBadgeClass(props.value)}`}>
          {props.value}
        </span>
      ),
      style: { alignSelf: 'center', paddingInline: '20px' }
    },
    {
      Header: 'Acciones',
      accessor: 'IdDespacho',
      filterable: false,
      sortable: false,
      Cell: props => {
        const estado = props.original && props.original.Estado;
        const filaEnProceso = !!despachosProcesando[String(props.value)];
        if (estado === 'PENDIENTE') {
          return (
            <div className="btn-group" role="group" style={{ gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => handleProcesar(props.value)}
                disabled={cargando || filaEnProceso}
              >
                {filaEnProceso ? 'Procesando...' : 'Despachar'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => handleAnular(props.value)}
                disabled={cargando || filaEnProceso}
              >
                Anular
              </button>
            </div>
          );
        }

        // Para EN_PROCESO y otros estados mostramos Ver
        return (
          <button
            type="button"
            onClick={() => handleVerDetalle(props.value)}
            className="btn btn-sm btn-info"
            disabled={cargando}
          >
            Ver
          </button>
        );
      },
      style: { alignSelf: 'center', paddingInline: '20px' }
    }
  ];

  // Botones de footer para los modales
  const abrirFooterButtons = [
    <button key="cancel" className="btn btn-danger" onClick={() => setModalAbrirTurno(false)}>Cancelar</button>,
    <button key="abrir" className="btn btn-success" onClick={handleAbrirTurno} disabled={cargando}>
      {cargando ? 'Abriendo...' : 'Abrir Turno'}
    </button>
  ];

  const detalleFooterButtons = [
    <button key="close" className="btn btn-secondary" onClick={handleCerrarDetalle}>Cerrar</button>
  ];
  
  if (selectedDespachoEstado === 'EN_PROCESO' && idDespachoSeleccionado) {
    detalleFooterButtons.push(
      <button key="terminar" className="btn btn-success" onClick={() => handleTerminar(idDespachoSeleccionado)} disabled={cargando}>
        {cargando ? 'Procesando...' : 'Terminar'}
      </button>
    );
  }

  return (
    <div className="container-fluid despacho-inicio mt-4">
      {/* Mensajes */}
      {error && (
        <div className="despacho-alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {exito && (
        <div className="despacho-alert alert-success">
          {exito}
        </div>
      )}

      {/* Sección principal (colapsable cuando hay turno activo) */}
      <div className="row mb-4 turno-seccion">
        <div className="col-12">
          {turnoActivo ? (
            <div className="card card-turno-activo card-turno-resumen">
              {turnoCollapsed ? (
                <div className="card-body p-2 d-flex align-items-center justify-content-between">
                  <div className="small text-muted">
                    Turno activo • Apertura: {new Date(turnoActivo.FechaApertura).toLocaleString()}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setTurnoCollapsed(false)}
                      title="Mostrar detalles"
                    >
                      <i className="fas fa-chevron-down"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <div>
                      <strong>Turno Activo</strong>
                      <div className="text-muted small">
                        Apertura: {new Date(turnoActivo.FechaApertura).toLocaleString()}
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-light mr-2"
                        onClick={() => setTurnoCollapsed(true)}
                        title="Ocultar detalles"
                      >
                        <i className="fas fa-chevron-up"></i>
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={handleCerrarTurno}
                        disabled={cargando}
                      >
                        Cerrar Turno
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="card card-turno-vacio card-turno-resumen">
              <div className="card-body text-center turno-vacio-body">
                <p className="text-muted mb-3">No hay turno activo. Abre uno para comenzar a despachar.</p>
                <button 
                  className="btn btn-verde-mfp btn-lg"
                  onClick={() => setModalAbrirTurno(true)}
                  disabled={cargando}
                >
                  + Abrir Turno
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Opciones si hay turno activo */}
      {turnoActivo && (
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card" style={{ backgroundColor: 'transparent' }}>
              <div className="card-body">
                {/* Filtros */}
                <div className="d-flex align-items-center  mb-3">
                  <div className="d-flex align-items-center mr-2">
                    <label className="small mb-0 mr-2">Desde</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ width: '150px' }}
                      value={fechaInicio}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFechaInicio(val);
                        if (turnoActivo && turnoActivo.IdTurnoDespacho) {
                          cargarDespachos(turnoActivo.IdTurnoDespacho, filtroEstado, val);
                        }
                      }}
                    />
                  </div>
                  <div className="btn-group" role="group">
                    <button 
                      type="button"
                      className={`btn btn-sm ${filtroEstado === 'PENDIENTE' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => handleFiltroEstado('PENDIENTE')}
                    >
                      Pendientes
                    </button>
                    <button 
                      type="button"
                      className={`btn btn-sm ${filtroEstado === 'EN_PROCESO' ? 'btn-info' : 'btn-outline-info'}`}
                      onClick={() => handleFiltroEstado('EN_PROCESO')}
                    >
                      En Proceso
                    </button>
                    <button 
                      type="button"
                      className={`btn btn-sm ${filtroEstado === 'DESPACHADO' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => handleFiltroEstado('DESPACHADO')}
                    >
                      Despachados
                    </button>
                    <button 
                      type="button"
                      className={`btn btn-sm ${filtroEstado === null ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleFiltroEstado(null)}
                    >
                      Todos ({despachos.length})
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-info"
                      onClick={() => setModalMisReportesOpen(true)}
                    >
                      Mis reportes
                    </button>
                  </div>

                    <div className="ml-auto">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                            if (turnoActivo && turnoActivo.IdTurnoDespacho) {
                                cargarDespachos(turnoActivo.IdTurnoDespacho, filtroEstado, fechaInicio);
                            }
                            }}
                            disabled={cargando}
                        >
                            <i className="fas fa-sync-alt"></i>
                            <span className="ml-2">{cargando ? 'Actualizando...' : 'Actualizar'}</span>
                        </button>
                    </div>
                </div>

                {/* Tabla de despachos con scroll horizontal para pantallas pequeñas */}
                <div className="despacho-table-wrapper">
                  <ReactTable
                    data={despachos}
                    columns={columns}
                    filterable
                    className="abed__table"
                    defaultPageSize={10}
                    pageSizeOptions={[5, 10, 20, 50]}
                    previousText="Anterior"
                    nextText="Siguiente"
                    loadingText="Cargando..."
                    noDataText="Sin datos"
                    pageText="Página"
                    rowsText="filas"
                    ofText="de"
                    loading={cargando}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para abrir turno */}
      <DespachoModal
        isOpen={modalAbrirTurno}
        title="Abrir Turno de Despacho"
        onClose={() => setModalAbrirTurno(false)}
        footerButtons={abrirFooterButtons}
      >
        <div className="form-group mb-0">
          <label htmlFor="observacionTurno">Observación (Opcional)</label>
          <textarea
            id="observacionTurno"
            className="form-control"
            rows="3"
            value={observacionTurno}
            onChange={(e) => setObservacionTurno(e.target.value)}
            placeholder="Ingrese observaciones sobre el turno"
            maxLength="500"
          />
        </div>
      </DespachoModal>

      {/* Modal para detalle de despacho */}
      <DespachoModal
        isOpen={modalDetalleOpen}
        title={idDespachoSeleccionado ? `Detalle de Productos - Despacho #${idDespachoSeleccionado}` : 'Detalle de Productos'}
        onClose={handleCerrarDetalle}
        footerButtons={detalleFooterButtons}
      >
        <DespachoDetalle idDespacho={idDespachoSeleccionado} />
      </DespachoModal>

      <DespachoModal
        isOpen={modalMisReportesOpen}
        title="Mis reportes"
        onClose={() => setModalMisReportesOpen(false)}
        footerButtons={[
          <button key="close-reports" className="btn btn-secondary" onClick={() => setModalMisReportesOpen(false)}>
            Cerrar
          </button>
        ]}
      >
        <div style={{ minWidth: '88vw', maxWidth: '92vw', padding: '0.5rem' }}>
          <MisReportes />
        </div>
      </DespachoModal>
    </div>
  );
};

export default DespachoTurno;